import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Landmark, Plus, RefreshCw, Trash2, ShieldCheck, ExternalLink,
    CheckCircle2, AlertTriangle, XCircle, Loader2, Link as LinkIcon,
} from "lucide-react";

const STATUS_STYLE = {
    Connected:            { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
    Syncing:              { cls: "bg-blue-50 text-blue-700 border-blue-200",           Icon: Loader2 },
    "Attention Needed":   { cls: "bg-amber-50 text-amber-700 border-amber-200",        Icon: AlertTriangle },
    "Reconnect Required": { cls: "bg-amber-50 text-amber-700 border-amber-200",        Icon: AlertTriangle },
    Disconnected:         { cls: "bg-stone-100 text-stone-700 border-stone-200",       Icon: XCircle },
    Failed:               { cls: "bg-rose-50 text-rose-700 border-rose-200",           Icon: XCircle },
    "Consent Expired":    { cls: "bg-rose-50 text-rose-700 border-rose-200",           Icon: XCircle },
};

export default function ConnectedAccounts() {
    const [conns, setConns] = useState(null);
    const [providers, setProviders] = useState([]);
    const [addOpen, setAddOpen] = useState(false);
    const [consent, setConsent] = useState(null);      // consent_summary during link
    const [linkToken, setLinkToken] = useState(null);
    const [form, setForm] = useState({ provider: "mock", institution_name: "", country: "IN" });
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const [c, p] = await Promise.all([
                api.get("/connect/connections"),
                api.get("/connect/providers"),
            ]);
            setConns(c.data.connections || []);
            setProviders(p.data.providers || []);
        } catch { toast.error("Failed to load connections"); }
    };
    useEffect(() => { load(); }, []);

    const openAdd = () => { setAddOpen(true); setConsent(null); setLinkToken(null); };

    const beginLink = async () => {
        if (!form.institution_name.trim()) { toast.error("Enter an institution name"); return; }
        setBusy(true);
        try {
            const r = await api.post("/connect/link-begin", form);
            setConsent(r.data.consent_summary);
            setLinkToken(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Link failed");
        } finally { setBusy(false); }
    };

    const completeLink = async () => {
        setBusy(true);
        try {
            await api.post("/connect/link-complete", {
                consent_id: linkToken.consent_id,
                public_token: `mock-public-${Date.now()}`,
            });
            toast.success("Institution connected · initial sync complete.");
            setAddOpen(false); setConsent(null); setLinkToken(null);
            setForm({ provider: "mock", institution_name: "", country: "IN" });
            await load();
        } catch { toast.error("Consent completion failed"); }
        finally { setBusy(false); }
    };

    const sync = async (c) => {
        try { await api.post(`/connect/connections/${c.id}/sync`); toast.success("Sync scheduled"); await load(); }
        catch { toast.error("Sync failed"); }
    };
    const disconnect = async (c) => {
        if (!window.confirm(`Disconnect ${c.institution_name}? Historical records stay.`)) return;
        try { await api.delete(`/connect/connections/${c.id}?purge=false`); toast.success("Disconnected"); await load(); }
        catch { toast.error("Disconnect failed"); }
    };

    if (conns === null) return <div className="p-8"><Loader2 size={16} className="animate-spin" /></div>;

    return (
        <div className="max-w-4xl mx-auto p-6 md:p-8" data-testid="connected-accounts">
            {/* Coming Soon banner while feature flag is OFF per market approval spec */}
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2" data-testid="connect-coming-soon">
                <ShieldCheck size={14} className="mt-0.5 shrink-0" />
                <div>
                    <b>VAULT Connect — Coming Soon.</b> Secure, consent-based syncing with supported financial institutions. This preview simulates the experience with a synthetic provider; live providers roll out per-market once security, privacy, legal and commercial reviews complete.
                </div>
            </div>
            <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-semibold">Settings</div>
                    <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                        <Landmark size={20} /> Connected Accounts
                    </h1>
                    <p className="text-sm text-stone-500 mt-1 max-w-xl">
                        Secure, consent-based financial syncing. VAULT never sees your banking password, PIN, OTP or CVV.
                    </p>
                </div>
                <Button onClick={openAdd} data-testid="add-connection-btn">
                    <Plus size={12} className="mr-1" /> Connect Institution
                </Button>
            </div>

            {conns.length === 0 && (
                <div className="rounded-2xl border border-dashed border-stone-300 p-10 text-center" data-testid="connect-empty">
                    <ShieldCheck size={22} className="mx-auto mb-3 text-indigo-600" />
                    <div className="text-lg font-semibold">No connected institutions yet</div>
                    <p className="text-sm text-stone-500 mt-1 max-w-md mx-auto">
                        Connect a bank, credit card or brokerage via an approved provider. Your credentials stay with the provider — VAULT only receives normalised data.
                    </p>
                    <Button className="mt-4" onClick={openAdd}><Plus size={12} className="mr-1" /> Connect your first</Button>
                </div>
            )}

            <div className="space-y-3" data-testid="connect-list">
                {conns.map((c) => {
                    const s = STATUS_STYLE[c.status] || STATUS_STYLE.Connected;
                    return (
                        <div key={c.id} className="rounded-xl border border-stone-200 bg-white p-4 flex items-center gap-3" data-testid={`conn-${c.id}`}>
                            <div className="rounded-lg bg-indigo-50 text-indigo-700 p-2"><Landmark size={16} /></div>
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-semibold">{c.institution_name}</div>
                                <div className="text-xs text-stone-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                                    <span>Provider: <b>{c.provider}</b></span>
                                    <span>{c.accounts_count} account{c.accounts_count === 1 ? "" : "s"}</span>
                                    <span>Last sync: {c.last_sync_at ? new Date(c.last_sync_at).toLocaleString() : "never"}</span>
                                    <span>Ownership: {c.ownership} · {c.visibility}</span>
                                </div>
                            </div>
                            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${s.cls}`}>
                                <s.Icon size={11} /> {c.status}
                            </span>
                            <Button size="sm" variant="ghost" onClick={() => sync(c)} data-testid={`sync-${c.id}`}>
                                <RefreshCw size={12} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => disconnect(c)} data-testid={`disconnect-${c.id}`}>
                                <Trash2 size={12} className="text-rose-600" />
                            </Button>
                        </div>
                    );
                })}
            </div>

            <div className="mt-6 rounded-xl border border-stone-200 bg-stone-50 p-4 text-xs text-stone-600 flex items-start gap-2">
                <ShieldCheck size={14} className="mt-0.5 shrink-0" />
                <div>
                    VAULT Connect delegates authentication to approved providers. Provider tokens are stored encrypted and never exposed to the frontend. You can revoke access at any time from this screen or from your provider&apos;s dashboard.
                </div>
            </div>

            {/* Add-connection dialog */}
            <Dialog open={addOpen} onOpenChange={(o) => !o && (setAddOpen(false), setConsent(null))}>
                <DialogContent className="max-w-lg" data-testid="link-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <LinkIcon size={14} /> Connect an institution
                        </DialogTitle>
                    </DialogHeader>
                    {!consent ? (
                        <div className="space-y-3">
                            <div>
                                <label className="text-[10px] uppercase tracking-wide text-stone-500">Institution</label>
                                <Input value={form.institution_name}
                                    onChange={(e) => setForm({ ...form, institution_name: e.target.value })}
                                    placeholder="HDFC · SBI · Chase · Wise …"
                                    data-testid="link-institution" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-[10px] uppercase tracking-wide text-stone-500">Provider</label>
                                    <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
                                        <SelectTrigger data-testid="link-provider"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase tracking-wide text-stone-500">Country</label>
                                    <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                                        <SelectTrigger data-testid="link-country"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {["IN","US","GB","EU","CA","AE","SG","AU"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="text-xs text-stone-500 bg-stone-50 border border-stone-200 rounded-md p-2 flex items-start gap-2">
                                <ShieldCheck size={12} className="mt-0.5 shrink-0" />
                                <span>You will be redirected to <b>{providers.find((p) => p.id === form.provider)?.name || form.provider}</b> to authenticate. VAULT never receives your credentials.</span>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <div className="text-sm font-semibold flex items-center gap-2 text-indigo-700">
                                <ShieldCheck size={14} /> Review consent
                            </div>
                            <div className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-sm space-y-1">
                                <Row k="Provider"    v={consent.provider} />
                                <Row k="Institution" v={consent.institution} />
                                <Row k="Data"        v={(consent.data_requested || []).join(", ")} />
                                <Row k="Purpose"     v={consent.purpose} />
                                <Row k="Frequency"   v={consent.frequency} />
                                {consent.revocation_url && (
                                    <Row k="Revocation" v={
                                        <a href={consent.revocation_url} target="_blank" rel="noreferrer"
                                           className="text-indigo-600 hover:underline inline-flex items-center gap-1">
                                            Manage at provider <ExternalLink size={10} />
                                        </a>
                                    } />
                                )}
                            </div>
                            <p className="text-xs text-stone-500">
                                By continuing, you consent to VAULT receiving normalised, read-only account and transaction data from this institution.
                            </p>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
                        {!consent ? (
                            <Button onClick={beginLink} disabled={busy} data-testid="link-continue">
                                {busy ? "Preparing…" : "Continue to consent"}
                            </Button>
                        ) : (
                            <Button onClick={completeLink} disabled={busy} data-testid="link-consent-give">
                                {busy ? "Connecting…" : "Give consent & connect"}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Row({ k, v }) {
    return (
        <div className="flex items-start gap-2 text-xs">
            <div className="w-24 text-stone-500 uppercase tracking-wide text-[10px] pt-0.5">{k}</div>
            <div className="text-stone-800 flex-1">{v || "—"}</div>
        </div>
    );
}
